import { useEffect, useLayoutEffect } from 'react';
import { Route, Routes, useLocation, useNavigationType } from 'react-router-dom';
import BlogArticlePage from './components/BlogArticlePage.jsx';
import BlogPage from './components/BlogPage.jsx';
import DoctorPage from './components/DoctorPage.jsx';
import GalleryPage from './components/GalleryPage.jsx';
import HomePage from './components/HomePage.jsx';
import Layout from './components/Layout.jsx';
import LegalPage from './components/LegalPage.jsx';
import NotFoundPage from './components/NotFoundPage.jsx';
import TreatmentPage from './components/TreatmentPage.jsx';
import { getPageMeta } from './data/seo.js';
import { clinic } from './data/site.js';

function upsertMeta(selector, createElement, apply) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = createElement();
    document.head.appendChild(element);
  }
  apply(element);
}

function absoluteUrl(path = '/') {
  if (/^https?:\/\//i.test(path)) return path;
  return `${clinic.siteUrl}${path.startsWith('/') ? path : `/${path}`}`;
}

function SeoSync() {
  const location = useLocation();

  useEffect(() => {
    const meta = getPageMeta(location.pathname);
    const canonical = absoluteUrl(meta.canonicalPath);
    const image = absoluteUrl(meta.image);

    document.title = meta.title;

    upsertMeta('meta[name="description"]', () => {
      const element = document.createElement('meta');
      element.setAttribute('name', 'description');
      return element;
    }, (element) => element.setAttribute('content', meta.description));

    upsertMeta('link[rel="canonical"]', () => {
      const element = document.createElement('link');
      element.setAttribute('rel', 'canonical');
      return element;
    }, (element) => element.setAttribute('href', canonical));

    const properties = {
      'og:type': meta.type || 'website',
      'og:title': meta.title,
      'og:description': meta.description,
      'og:url': canonical,
      'og:image': image
    };

    Object.entries(properties).forEach(([property, content]) => {
      upsertMeta(`meta[property="${property}"]`, () => {
        const element = document.createElement('meta');
        element.setAttribute('property', property);
        return element;
      }, (element) => element.setAttribute('content', content));
    });

    const twitter = {
      'twitter:card': 'summary_large_image',
      'twitter:title': meta.title,
      'twitter:description': meta.description,
      'twitter:image': image
    };

    Object.entries(twitter).forEach(([name, content]) => {
      upsertMeta(`meta[name="${name}"]`, () => {
        const element = document.createElement('meta');
        element.setAttribute('name', name);
        return element;
      }, (element) => element.setAttribute('content', content));
    });

    document.querySelectorAll('script[data-react-seo="jsonld"]').forEach((script) => script.remove());
    meta.schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.reactSeo = 'jsonld';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, [location.pathname]);

  return null;
}

const scrollPositions = new Map();
const HEADER_SCROLL_OFFSET = 76;
const SCROLL_STORAGE_KEY = 'my-dentist-scroll-positions';
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

function getPathScrollKey(location) {
  return `${location.pathname}${location.search}`;
}

function getCurrentHistoryIndex() {
  try {
    return window.history.state?.idx ?? null;
  } catch {
    return null;
  }
}

function getStorageScrollKey(location, historyIndex = getCurrentHistoryIndex()) {
  const entryKey = historyIndex ?? 'entry';
  return `${entryKey}:${getPathScrollKey(location)}`;
}

function getWindowLocationSnapshot() {
  return {
    pathname: window.location.pathname,
    search: window.location.search,
    key: window.history.state?.key || 'default'
  };
}

function readStoredScrollPositions() {
  try {
    return JSON.parse(window.sessionStorage.getItem(SCROLL_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function getSavedScrollPosition(location, { storageScrollKey = getStorageScrollKey(location), allowFallback = false } = {}) {
  const stored = readStoredScrollPositions();
  if (stored[storageScrollKey]) return stored[storageScrollKey];

  const indexedPosition = scrollPositions.get(storageScrollKey);
  if (indexedPosition) return indexedPosition;

  if (!allowFallback) return null;

  const keyPosition = location.key && location.key !== 'default' ? scrollPositions.get(location.key) : null;
  if (keyPosition) return keyPosition;

  const pathSuffix = `:${getPathScrollKey(location)}`;
  const fallbackEntry = Object.entries(stored).find(([key]) => key.endsWith(pathSuffix));
  return fallbackEntry?.[1] || null;
}

function persistScrollPositions(entries) {
  try {
    const stored = readStoredScrollPositions();
    entries.forEach(([key, position]) => {
      stored[key] = position;
    });
    window.sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Scroll restoration is an enhancement; ignore storage restrictions.
  }
}

function getHashTarget(hash) {
  let id = hash.slice(1);
  try {
    id = decodeURIComponent(id);
  } catch {
    return null;
  }

  if (!id) return null;
  const target = document.getElementById(id);
  if (target) return target;

  try {
    return document.querySelector(hash);
  } catch {
    return null;
  }
}

function scrollPageTo({ left = 0, top, behavior = 'auto', instant = false }) {
  const root = document.documentElement;
  const previousRootScrollBehavior = root.style.scrollBehavior;
  const previousBodyScrollBehavior = document.body.style.scrollBehavior;

  if (instant) {
    root.style.scrollBehavior = 'auto';
    document.body.style.scrollBehavior = 'auto';
  }

  window.scrollTo({ left, top, behavior });

  if (behavior === 'auto') {
    document.documentElement.scrollTop = top;
    document.body.scrollTop = top;
  }

  if (instant) {
    root.style.scrollBehavior = previousRootScrollBehavior;
    document.body.style.scrollBehavior = previousBodyScrollBehavior;
  }
}

function scrollHashIntoView(hash) {
  const target = getHashTarget(hash);
  if (!target) return false;

  const top = target.getBoundingClientRect().top + window.scrollY - HEADER_SCROLL_OFFSET;
  scrollPageTo({ top: Math.max(0, top), behavior: 'auto' });
  return true;
}

function suppressScrollSavingBriefly() {
  window.__myDentistSuppressScrollSave = true;
  window.setTimeout(() => {
    window.__myDentistSuppressScrollSave = false;
  }, 160);
}

function ScrollManager() {
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (!('scrollRestoration' in window.history)) return undefined;

    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'auto';

    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  useEffect(() => {
    function restorePopStateScroll() {
      const currentLocation = getWindowLocationSnapshot();
      const storageScrollKey = getStorageScrollKey(currentLocation);
      const savedPosition = getSavedScrollPosition(currentLocation, {
        storageScrollKey,
        allowFallback: true
      });

      if (savedPosition) {
        suppressScrollSavingBriefly();
        scrollPageTo({ left: savedPosition.x, top: savedPosition.y, behavior: 'auto', instant: true });
      }
    }

    window.addEventListener('popstate', restorePopStateScroll, true);
    return () => window.removeEventListener('popstate', restorePopStateScroll, true);
  }, []);

  useIsomorphicLayoutEffect(() => {
    let frameId = 0;
    const retryIds = [];

    function applyScroll() {
      const currentHash = location.hash || window.location.hash;
      const storageScrollKey = getStorageScrollKey(location);

      if (currentHash) {
        scrollHashIntoView(currentHash);
        return;
      }

      if (navigationType === 'POP') {
        const savedPosition = getSavedScrollPosition(location, {
          storageScrollKey,
          allowFallback: true
        });
        if (savedPosition) {
          scrollPageTo({ left: savedPosition.x, top: savedPosition.y, behavior: 'auto', instant: true });
          return;
        }

        scrollPageTo({ left: 0, top: 0, behavior: 'auto', instant: true });
        return;
      }

      scrollPageTo({ left: 0, top: 0, behavior: 'auto', instant: true });
    }

    applyScroll();
    frameId = window.requestAnimationFrame(applyScroll);
    retryIds.push(window.setTimeout(applyScroll, 120));
    retryIds.push(window.setTimeout(applyScroll, 320));

    return () => {
      window.cancelAnimationFrame(frameId);
      retryIds.forEach((retryId) => window.clearTimeout(retryId));
    };
  }, [location.key, location.pathname, location.search, location.hash, navigationType]);

  useIsomorphicLayoutEffect(() => {
    let ticking = false;
    const storageScrollKey = getStorageScrollKey(location);
    let lastPosition = {
      x: window.scrollX,
      y: window.scrollY
    };

    function storePosition(position, { persist = false } = {}) {
      const entries = [
        [storageScrollKey, position],
        [location.key, position],
        [getPathScrollKey(location), position]
      ];

      entries.forEach(([key, value]) => scrollPositions.set(key, value));
      if (persist) {
        persistScrollPositions([[storageScrollKey, position]]);
      }
    }

    function saveCurrentPosition({ persist = false } = {}) {
      lastPosition = {
        x: window.scrollX,
        y: window.scrollY
      };
      storePosition(lastPosition, { persist });
      ticking = false;
    }

    function onScroll() {
      if (window.__myDentistSuppressScrollSave) return;
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(saveCurrentPosition);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    const savePersistentPosition = () => saveCurrentPosition({ persist: true });
    window.addEventListener('pagehide', savePersistentPosition);

    return () => {
      storePosition(lastPosition, { persist: true });
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', savePersistentPosition);
    };
  }, [location.key, location.pathname, location.search]);

  return null;
}

export default function App() {
  return (
    <>
      <SeoSync />
      <ScrollManager />
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/dentists/:slug" element={<DoctorPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogArticlePage />} />
          <Route path="/treatments/:slug" element={<TreatmentPage />} />
          <Route path="/privacy-policy/" element={<LegalPage />} />
          <Route path="/cookie-policy/" element={<LegalPage />} />
          <Route path="/accessibility/" element={<LegalPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </>
  );
}
